import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { RRuleSet, rrulestr } from 'rrule'
import { DashboardRoom, ScheduledSessionOfUser, useAppContext } from '@/AppContext'
import { Loader } from '@/components/Loader'
import CalendarIcon from '@/CustomIcons/CalendarIcon'
import LeftChevronIcon from '@/CustomIcons/LeftChevron'
import RightChevronIcon from '@/CustomIcons/RightChervron'
import { toRruleDate } from '@/pages/ScheduleSessionPage/ScheduleSessionLogic'
import Api from '@/services/Api'
import { Button } from '@/UIComponents2/Button'
import { getCurrentIanaTimezone } from '@/utils'
import { ScheduledSessionCard } from './ScheduledSessionCard'
import { frontpageReloadTrigger } from './useFrontpage'
import { EmptyTab } from './EmptyTab'
import { RadioGroup } from '@/UIComponents2/RadioGroup'
import CrossPlatformDialog from '@/dialogs/components/CrossPlatformDialog'
import { DialogBody } from '@/components/Dialog'
import DialogActionButtonsSection from '@/dialogs/components/DialogActionButtonsSection'
import useScheduledSessionsFilter from '@/services/useScheduledSessionsFilter'
import Tooltip from '@/UIComponents2/Tooltip'
import CalendarAddIcon from '@/CustomIcons/CalendarAddIcon'
import useAnalytics from '@/services/useAnalytics'
import { AnalyticsEvents } from '@/services/AnalyticsService'
import { cn } from '@/utlis/cn'

type ScheduledSessionsProps = {
    scheduledSessionsFilter: ReturnType<typeof useScheduledSessionsFilter>
    loading: boolean
}
export function ScheduledSessions({ scheduledSessionsFilter, loading }: ScheduledSessionsProps) {
    const { t } = useTranslation()
    const [_appState, appDispatch] = useAppContext()
    const { logUserActivityWithContext } = useAnalytics()
    const loadingEl = loading && (
        <div className="w-full pt-[100px] flex items-center justify-center">
            <Loader className="bg-neutral-700" width="40%" />
        </div>
    )

    const [showRoomUpdateConfirmationDialog, setShowRoomUpdateConfirmationDialog] = useState<{
        session: ScheduledSessionOfUser
    } | null>(null)
    const [showRoomDeleteConfirmationDialog, setShowRoomDeleteConfirmationDialog] = useState<{
        session: ScheduledSessionOfUser | DashboardRoom
    } | null>(null)

    const handleEditSession = (session: ScheduledSessionOfUser) => {
        if (session.scheduledRrule) {
            setShowRoomUpdateConfirmationDialog({ session })
        } else {
            appDispatch({
                type: 'update_app_state',
                value: {
                    showScheduleSessionDialog: {
                        isOpen: true,
                        editingSession: session,
                        isSingleInSeries: false,
                    },
                },
            })
        }
    }

    const handleScheduleSession = () => {
        logUserActivityWithContext(AnalyticsEvents.CLICKED_SCHEDULE_SESSION_BUTTON)
        appDispatch({
            type: 'update_app_state',
            value: {
                showScheduleSessionDialog: { isOpen: true },
            },
        })
    }

    const handleDeleteSession = (session: ScheduledSessionOfUser) => {
        setShowRoomDeleteConfirmationDialog({ session })
    }

    const { windowLabel, handleToday, handleNextWeek, handlePrevWeek } = scheduledSessionsFilter

    const sessionEls = scheduledSessionsFilter.sessionsInWindow.map((session) => (
        <ScheduledSessionCard
            key={session.joinCode}
            session={session}
            onEdit={() => handleEditSession(session)}
            onDelete={() => handleDeleteSession(session)}
        />
    ))

    const iconButtonClasses = 'flex opacity-85 hover:opacity-100 hover:scale-120 transition-transform'

    return (
        <div className="h-[calc(100vh-123px-30px)] grid grid-rows-[min-content_1fr] gap-[8px] pt-[25px] px-[8px] ">
            <div className="flex justify-between items-center px-2 pb-2">
                <div className="flex gap-1 items-center">
                    <Tooltip text={t('Previous Week')}>
                        <Button className={iconButtonClasses} onClick={() => handlePrevWeek()}>
                            <LeftChevronIcon style={{ fontSize: '1.3rem' }} />
                        </Button>
                    </Tooltip>
                    <Tooltip text={t('Today')}>
                        <Button className={iconButtonClasses} onClick={() => handleToday()}>
                            <CalendarIcon style={{ fontSize: '1.4rem' }} />
                        </Button>
                    </Tooltip>
                    <Tooltip text={t('Next Week')}>
                        <Button className={iconButtonClasses} onClick={() => handleNextWeek()}>
                            <RightChevronIcon style={{ fontSize: '1.3rem' }} />
                        </Button>
                    </Tooltip>
                    <div className="t-base-bold pl-1">{windowLabel}</div>
                </div>
                <div className="pr-1">
                    <Tooltip text={t('Schedule a session')}>
                        <Button className={iconButtonClasses} onClick={() => handleScheduleSession()}>
                            <CalendarAddIcon style={{ fontSize: '1.4rem' }} />
                        </Button>
                    </Tooltip>
                </div>
            </div>
            <div
                className={cn(
                    'flex flex-col gap-[8px] overflow-y-auto overflow-x-hidden pb-[10px]',
                    sessionEls.length > 0 && !loading ? 'px-[8px]' : ''
                )}
            >
                {loadingEl}
                {sessionEls.length === 0 && !loading && (
                    <EmptyTab
                        icon={<CalendarIcon />}
                        text={t('No sessions scheduled')}
                        subtext={t('Schedule a session')}
                    />
                )}
                {!loadingEl && sessionEls}

                {showRoomUpdateConfirmationDialog && (
                    <CrossPlatformDialog
                        key={'Dialog:UpcomingSessionsPanelRoomDeleteConfirmationDialog'}
                        forceBrowserModal
                        onDefaultClose={() => setShowRoomUpdateConfirmationDialog(null)}
                    >
                        <RoomDeleteConfirmationDialog
                            session={showRoomUpdateConfirmationDialog!.session}
                            type="update"
                            onResult={(isSingleInSeries: boolean) => {
                                const { session } = showRoomUpdateConfirmationDialog!
                                appDispatch({
                                    type: 'update_app_state',
                                    value: {
                                        showScheduleSessionDialog: {
                                            isOpen: true,
                                            editingSession: session,
                                            isSingleInSeries: isSingleInSeries,
                                        },
                                    },
                                })
                            }}
                            onClose={() => setShowRoomUpdateConfirmationDialog(null)}
                        />
                    </CrossPlatformDialog>
                )}
                {showRoomDeleteConfirmationDialog && (
                    <CrossPlatformDialog
                        key={'Dialog:UpcomingSessionsPanelRoomDeleteConfirmationDialog'}
                        forceBrowserModal
                        onDefaultClose={() => setShowRoomDeleteConfirmationDialog(null)}
                    >
                        <RoomDeleteConfirmationDialog
                            session={showRoomDeleteConfirmationDialog!.session}
                            type={'delete'}
                            onResult={async (isSingleInSeries: boolean) => {
                                const { session } = showRoomDeleteConfirmationDialog!

                                const scheduledRrule = 'scheduledRrule' in session ? session.scheduledRrule : null
                                const scheduledStartTime =
                                    'scheduledStartTime' in session ? session.scheduledStartTime : null
                                const roomUrn = 'roomUrn' in session ? session.roomUrn : session._id

                                if (scheduledRrule && scheduledStartTime && isSingleInSeries) {
                                    const rrule = rrulestr(scheduledRrule.icalStr)
                                    const ruleSet = new RRuleSet()
                                    ruleSet.rrule(rrule)

                                    const tzid = ruleSet.tzid() || getCurrentIanaTimezone()
                                    const exdate = toRruleDate(scheduledStartTime, tzid)
                                    ruleSet.exdate(exdate)

                                    const scheduledRruleNext = {
                                        ...scheduledRrule,
                                        icalStr: ruleSet.toString(),
                                    }

                                    await Api.updateRoom(roomUrn, { scheduledRrule: scheduledRruleNext })
                                } else {
                                    await Api.deleteRoom(roomUrn)
                                }
                                frontpageReloadTrigger.trigger()
                            }}
                            onClose={() => setShowRoomDeleteConfirmationDialog(null)}
                        />
                    </CrossPlatformDialog>
                )}
            </div>
        </div>
    )
}

function RoomDeleteConfirmationDialog({
    session,
    type,
    onResult,
    onClose,
}: {
    session: ScheduledSessionOfUser | DashboardRoom
    type: 'update' | 'delete'
    onResult: (isSingleInSeries: boolean) => void
    onClose: () => void
}) {
    const { t } = useTranslation()

    const [updateTarget, setUpdateTarget] = useState<'single' | 'series'>('single')

    const scheduledRrule = 'scheduledRrule' in session ? session.scheduledRrule : null

    const onEdit = () => {
        const isSingleInSeries = !!scheduledRrule && updateTarget === 'single'
        onResult(isSingleInSeries)
        onClose()
    }

    let body = null
    if (scheduledRrule) {
        const msg =
            type === 'delete'
                ? 'This is a recurring session. What would you like to delete?'
                : 'This is a recurring session. What would you like to edit?'
        body = (
            <>
                <div className="pb-4 pt-3">{msg}</div>
                <div className="flex flex-col">
                    <RadioGroup
                        items={[
                            { label: 'This session only', value: 'single' },
                            { label: 'All sessions in this series', value: 'all' },
                        ]}
                        value={updateTarget}
                        onChange={(val) => setUpdateTarget(val as 'single' | 'series')}
                    />
                </div>
            </>
        )
    } else {
        body = (
            <div>
                Are you sure you want to delete <span className="text-muse-yellow">{session.name}</span>?
            </div>
        )
    }

    return (
        <>
            <DialogBody>{body}</DialogBody>

            <DialogActionButtonsSection
                leftButtonText={t('Cancel')}
                rightButtonText={type === 'delete' ? t('Delete') : t('Update')}
                onLeftButtonClicked={onClose}
                onRightButtonClicked={onEdit}
            />
        </>
    )
}
